import React, { useState } from "react";
import { athleteProfileApi } from "../api/routes";
import { Button, Card, Input, Select, TextArea, Toast, TwoColumn } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const distanceOptions = ["FIVE_K", "TEN_K", "HALF_MARATHON", "MARATHON", "ULTRA_MARATHON", "OFF_SEASON"];
const listFields = [
  ["trainingExperience", "Training experience"],
];

const blankProfile = {
  userId: "",
  trainingExperience: [],
  maxMileage: 0,
  availability: {},
  currentWorkoutPlanId: "",
  personalRecords: [],
  trainingHistorySummary: "",
  seasonalSummary: "",
  lastUpdated: "",
  experienceLevel: "",
  trainingArchetype: "",
  trainingBias: "",
};

const blankMemory = {
  preferences: [],
  injuries: [],
  constraints: [],
  intents: [],
  stimulusProfiles: [],
};

function dayKeyVariants(day) {
  return [day, day.toLowerCase(), day.slice(0, 3), day.slice(0, 3).toLowerCase()];
}

function availabilityValue(availability, day) {
  const source = availability || {};
  const key = dayKeyVariants(day).find((variant) => source[variant] !== undefined);
  return key ? source[key] : "";
}

function parseInteger(value, fallback = 0) {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function cleanList(value) {
  return (value || []).map((item) => String(item || "").trim()).filter(Boolean);
}

function cleanStringMap(value) {
  return Object.fromEntries(
    Object.entries(value || {})
      .map(([key, item]) => [String(key || "").trim(), String(item || "").trim()])
      .filter(([key, item]) => key && item)
  );
}

function cleanObjectList(value) {
  return (value || []).filter((item) =>
    Object.values(item || {}).some((fieldValue) => fieldValue !== "" && fieldValue !== null && fieldValue !== undefined)
  );
}

function cleanPersonalRecords(value) {
  return (value || [])
    .map((record) => ({
      distance: record.distance || null,
      time: String(record.time || "").trim(),
      date: String(record.date || "").trim() || null,
      context: String(record.context || "").trim() || null,
    }))
    .filter((record) => record.distance && record.time);
}

function Section({ title, children, accent = false }) {
  return (
    <section
      style={{
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        border: accent ? "1px solid rgba(25,164,99,0.2)" : "1px solid rgba(15,23,42,0.08)",
        background: accent
          ? "linear-gradient(135deg, rgba(226,251,232,0.62), rgba(255,255,255,0.92))"
          : "rgba(255,255,255,0.58)",
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 10 }}>{title}</div>
      {children}
    </section>
  );
}

export default function AthleteProfilePanel() {
  const [profile, setProfile] = useState(blankProfile);
  const [memory, setMemory] = useState(blankMemory);
  const [lookupId, setLookupId] = useState("");
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState("search");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [isExisting, setIsExisting] = useState(false);
  const [panelState, setPanelState] = usePanelCache("athlete");
  const results = panelState.results || [];
  const showSearch = panelState.showSearch ?? true;
  const [searching, setSearching] = useState(false);

  const patchProfile = (patch) => setProfile((current) => ({ ...current, ...patch }));
  const patchMemory = (patch) => setMemory((current) => ({ ...current, ...patch }));

  const loadProfile = (data = {}, existing = false) => {
    const athleteProfile = data.athleteProfile || data;
    const athleteMemory = data.athleteMemory || blankMemory;
    const merged = { ...blankProfile, ...(athleteProfile || {}) };
    setIsExisting(existing);
    setProfile({
      ...merged,
      availability: merged.availability || {},
    });
    setMemory({
      ...blankMemory,
      ...athleteMemory,
      preferences: athleteMemory.preferences || [],
      injuries: athleteMemory.injuries || [],
      constraints: athleteMemory.constraints || [],
      intents: athleteMemory.intents || [],
      stimulusProfiles: athleteMemory.stimulusProfiles || [],
    });
  };

  const buildPayload = () => {
    return {
      athleteProfile: {
        ...profile,
        userId: profile.userId,
        trainingExperience: cleanList(profile.trainingExperience),
        maxMileage: parseInteger(profile.maxMileage, 0),
        availability: cleanStringMap(profile.availability),
        currentWorkoutPlanId: profile.currentWorkoutPlanId || "",
        personalRecords: cleanPersonalRecords(profile.personalRecords),
        trainingHistorySummary: profile.trainingHistorySummary || "",
        seasonalSummary: profile.seasonalSummary || "",
        lastUpdated: profile.lastUpdated || "",
        experienceLevel: profile.experienceLevel || null,
        trainingArchetype: profile.trainingArchetype || null,
        trainingBias: profile.trainingBias || null,
      },
      athleteMemory: {
        preferences: cleanObjectList(memory.preferences).map((preference) => ({
          ...preference,
          normalizedKey: preference.normalizedKey || `TRAINING|${String(preference.summary || "").trim().toLowerCase().replace(/\s+/g, " ")}`,
          scope: preference.scope || "TRAINING",
          polarity: preference.polarity || "PREFERS",
          strength: preference.strength || "MODERATE",
          status: preference.status || "ACTIVE",
          confidence: preference.confidence ?? 1,
        })),
        injuries: cleanObjectList(memory.injuries).map((injury) => ({
          ...injury,
          canonicalArea: injury.canonicalArea || injury.bodyLocation || injury.description,
          severity: String(injury.severity || "MILD").toUpperCase(),
          status: String(injury.status || "ONGOING").toUpperCase(),
          confidence: injury.confidence ?? 1,
        })),
        constraints: cleanObjectList(memory.constraints).map((constraint) => ({
          ...constraint,
          summary: constraint.summary || constraint.description,
          status: constraint.status || "ACTIVE",
          expiresAt: constraint.expiresAt === "" ? null : Number(constraint.expiresAt),
        })),
      },
    };
  };

  const handleFetch = async () => {
    setStatus("Fetching...");
    setSearching(true);
    try {
      const res = await athleteProfileApi.byUser(lookupId);
      loadProfile(res.data, true);
      setPanelState({ results: res.data ? [res.data] : [], showSearch: false });
      setStatus("Loaded profile");
    } catch (e) {
      setStatus("Error fetching profile");
      setToast({ message: e.message || "Error fetching profile", tone: "error" });
      setPanelState({ results: [] });
    } finally {
      setSearching(false);
    }
  };

  const handleCreate = async () => {
    if (!profile.userId) return setStatus("userId required to create");
    setSaving(true);
    setStatus("Saving...");
    try {
      const payload = buildPayload();
      const res = await athleteProfileApi.create(payload);
      const created = res.data || payload;
      setPanelState({ results: [created, ...results] });
      setStatus("Created");
      setMode("search");
      setToast({ message: "Profile created", tone: "success" });
    } catch (e) {
      setStatus("Error creating");
      setToast({ message: e.message || "Error creating", tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!profile.userId) return setStatus("userId required to update");
    setSaving(true);
    setStatus("Updating...");
    try {
      const payload = buildPayload();
      const res = await athleteProfileApi.update(profile.userId, payload);
      const updated = res.data || payload;
      setPanelState({ results: results.map((p) => (
        (p.athleteProfile || p).userId === (updated.athleteProfile || updated).userId ? updated : p
      )) });
      setStatus("Updated");
      setMode("search");
      setToast({ message: "Profile updated", tone: "success" });
    } catch (e) {
      setStatus("Error updating");
      setToast({ message: e.message || "Error updating", tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  const renderListEditor = (field, label) => {
    const values = profile[field] || [];
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ fontWeight: 700 }}>{label}</div>
          <Button tone="ghost" onClick={() => patchProfile({ [field]: [...values, ""] })}>+ Add</Button>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {values.map((item, index) => (
            <div key={index} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8, alignItems: "start" }}>
              <TextArea
                label={`${label} ${index + 1}`}
                rows={2}
                value={item || ""}
                onChange={(value) => {
                  const next = [...values];
                  next[index] = value;
                  patchProfile({ [field]: next });
                }}
              />
              <Button tone="ghost" onClick={() => patchProfile({ [field]: values.filter((_, itemIndex) => itemIndex !== index) })}>
                Remove
              </Button>
            </div>
          ))}
          {values.length === 0 && <div style={{ color: "var(--stone-700)", padding: 10 }}>No values.</div>}
        </div>
      </div>
    );
  };

  const renderPersonalRecordsEditor = () => (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Personal records</div>
      {(profile.personalRecords || []).map((record, index) => (
        <div key={index} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr)) auto", gap: 8, marginBottom: 8, alignItems: "end" }}>
          <Select label="Distance" value={record.distance || ""} options={distanceOptions} onChange={(value) => {
            const next = [...(profile.personalRecords || [])];
            next[index] = { ...next[index], distance: value };
            patchProfile({ personalRecords: next });
          }} />
          <Input label="Time" value={record.time || ""} onChange={(value) => {
            const next = [...(profile.personalRecords || [])];
            next[index] = { ...next[index], time: value };
            patchProfile({ personalRecords: next });
          }} />
          <Input label="Date" type="date" value={record.date || ""} onChange={(value) => {
            const next = [...(profile.personalRecords || [])];
            next[index] = { ...next[index], date: value };
            patchProfile({ personalRecords: next });
          }} />
          <Input label="Context" value={record.context || ""} onChange={(value) => {
            const next = [...(profile.personalRecords || [])];
            next[index] = { ...next[index], context: value };
            patchProfile({ personalRecords: next });
          }} />
          <Button tone="ghost" onClick={() => patchProfile({ personalRecords: (profile.personalRecords || []).filter((_, itemIndex) => itemIndex !== index) })}>
            Remove
          </Button>
        </div>
      ))}
      <Button tone="ghost" onClick={() => patchProfile({ personalRecords: [...(profile.personalRecords || []), { distance: "FIVE_K", time: "", date: "", context: "" }] })}>
        + Add PR
      </Button>
    </div>
  );

  const renderSearch = () => (
    <Card
      title="Athlete profiles"
      action={
        <div style={{ display: "flex", gap: 8 }}>
          <Button tone="ghost" onClick={() => setPanelState({ showSearch: !showSearch })}>
            {showSearch ? "Hide search" : "Show search"}
          </Button>
          <Button onClick={() => { loadProfile(blankProfile, false); setMode("edit"); }}>+ New athlete</Button>
        </div>
      }
    >
      {showSearch && (
        <Card title="Lookup athlete">
          <Input label="User ID" value={lookupId} onChange={setLookupId} />
          <Button onClick={handleFetch} disabled={!lookupId || saving} loading={searching}>Fetch Profile</Button>
        </Card>
      )}
      {results.length === 0 ? (
        <div style={{ padding: 12, color: "var(--stone-700)" }}>No results yet. Run a search.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
          <thead>
            <tr style={{ background: "rgba(25,164,99,0.08)" }}>
              <th style={{ padding: 8, textAlign: "left" }}>User ID</th>
              <th style={{ padding: 8, textAlign: "left" }}>Bias</th>
              <th style={{ padding: 8, textAlign: "left" }}>Preferences</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result) => {
              const p = result.athleteProfile || result;
              const resultMemory = result.athleteMemory || {};
              return (
              <tr
                key={p.userId}
                style={{ borderBottom: "1px solid rgba(15,23,42,0.06)", cursor: "pointer" }}
                onClick={() => { loadProfile(result, true); setMode("edit"); }}
              >
                <td style={{ padding: 8, fontFamily: "monospace" }}>{p.userId}</td>
                <td style={{ padding: 8 }}>{p.trainingBias || "n/a"}</td>
                <td style={{ padding: 8 }}>{(resultMemory.preferences || []).map((preference) => preference.summary).join(", ")}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );

  const renderEdit = () => (
    <Card
      title={isExisting ? "Edit athlete" : "Create athlete"}
      action={<Button tone="ghost" onClick={() => { setMode("search"); }}>Back to search</Button>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px,1fr))", gap: 12 }}>
        {isExisting ? (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>User ID</div>
            <div style={{ padding: "10px 12px", borderRadius: 12, background: "rgba(15,23,42,0.06)", fontFamily: "monospace", fontWeight: 700 }}>
              {profile.userId}
            </div>
          </div>
        ) : (
          <Input label="User ID" value={profile.userId} onChange={(value) => patchProfile({ userId: value })} />
        )}
        <Input label="Max mileage" type="number" value={profile.maxMileage ?? ""} onChange={(value) => patchProfile({ maxMileage: value })} />
        <Input label="Current workout plan ID" value={profile.currentWorkoutPlanId || ""} onChange={(value) => patchProfile({ currentWorkoutPlanId: value })} />
        <Input label="Last updated" value={profile.lastUpdated || ""} onChange={(value) => patchProfile({ lastUpdated: value })} />
        <Select label="Experience level" value={profile.experienceLevel || ""} onChange={(value) => patchProfile({ experienceLevel: value })} options={["BEGINNER", "INTERMEDIATE", "ADVANCED", "ELITE"]} />
        <Select label="Training archetype" value={profile.trainingArchetype || ""} onChange={(value) => patchProfile({ trainingArchetype: value })} options={["GENERAL_FITNESS", "ROAD_SPEED", "ROAD_ENDURANCE", "TRAIL_ULTRA", "HYBRID_ENDURANCE", "RETURN_TO_RUNNING"]} />
        <Select label="Training bias" value={profile.trainingBias || ""} onChange={(value) => patchProfile({ trainingBias: value })} options={["CAUTIOUS", "NORMAL", "AGGRESSIVE"]} />
      </div>

      <Section title="Availability" accent>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 8 }}>
          {daysOfWeek.map((day) => (
            <Input
              key={day}
              label={day}
              value={availabilityValue(profile.availability, day)}
              onChange={(value) => {
                const next = { ...(profile.availability || {}) };
                dayKeyVariants(day).forEach((variant) => {
                  if (variant !== day) delete next[variant];
                });
                next[day] = value;
                patchProfile({ availability: next });
              }}
            />
          ))}
        </div>
      </Section>

      {listFields.map(([field, label]) => (
        <Section key={field} title={label}>
          {renderListEditor(field, label)}
        </Section>
      ))}

      <Section title="Preferences">
        {(memory.preferences || []).map((preference, index) => (
          <div key={preference.preferenceId || index} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 8, marginBottom: 8 }}>
            <TextArea label={`Preference ${index + 1}`} rows={2} value={preference.summary || ""} onChange={(value) => {
              const next = [...(memory.preferences || [])];
              next[index] = { ...next[index], summary: value };
              patchMemory({ preferences: next });
            }} />
            <Button tone="ghost" onClick={() => patchMemory({ preferences: (memory.preferences || []).filter((_, itemIndex) => itemIndex !== index) })}>Remove</Button>
          </div>
        ))}
        <Button tone="ghost" onClick={() => patchMemory({ preferences: [...(memory.preferences || []), { summary: "", scope: "TRAINING", polarity: "PREFERS", strength: "MODERATE", status: "ACTIVE", confidence: 1 }] })}>
          + Add preference
        </Button>
      </Section>

      <Section title="Injuries" accent>
        {(memory.injuries || []).map((injury, index) => (
          <div key={index} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))", gap: 8, marginBottom: 8, background: "rgba(15,23,42,0.03)", padding: 10, borderRadius: 10 }}>
            <Input label="Injury ID" value={injury.injuryId || ""} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], injuryId: value };
              patchMemory({ injuries: next });
            }} />
            <Input label="Canonical area" value={injury.canonicalArea || ""} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], canonicalArea: value };
              patchMemory({ injuries: next });
            }} />
            <Input label="Description" value={injury.description || ""} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], description: value };
              patchMemory({ injuries: next });
            }} />
            <Select label="Severity" value={injury.severity || ""} options={["MILD", "MODERATE", "SEVERE", "UNKNOWN"]} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], severity: value };
              patchMemory({ injuries: next });
            }} />
            <Select label="Status" value={injury.status || ""} options={["NEW", "ONGOING", "HEALING", "RECURRING", "RESOLVED", "UNKNOWN"]} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], status: value };
              patchMemory({ injuries: next });
            }} />
            <Input label="Start date" value={injury.startDate || ""} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], startDate: value };
              patchMemory({ injuries: next });
            }} />
            <Input label="End date" value={injury.endDate || ""} onChange={(value) => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], endDate: value };
              patchMemory({ injuries: next });
            }} />
            <Button tone="ghost" onClick={() => {
              const next = [...(memory.injuries || [])];
              next[index] = { ...next[index], status: "RESOLVED", endDate: next[index].endDate || new Date().toISOString().slice(0, 10) };
              patchMemory({ injuries: next });
            }}>Mark resolved</Button>
          </div>
        ))}
        <Button tone="ghost" onClick={() => patchMemory({ injuries: [...(memory.injuries || []), { injuryId: "", canonicalArea: "", description: "", severity: "MILD", status: "ONGOING", startDate: "", endDate: "", confidence: 1 }] })}>
          + Add injury
        </Button>
      </Section>

      <Section title="Performance maps">
        {renderPersonalRecordsEditor()}
      </Section>

      <Section title="Coach summaries" accent>
        <TextArea label="Training history summary" rows={5} value={profile.trainingHistorySummary || ""} onChange={(value) => patchProfile({ trainingHistorySummary: value })} />
        <TextArea label="Seasonal summary" rows={5} value={profile.seasonalSummary || ""} onChange={(value) => patchProfile({ seasonalSummary: value })} />
      </Section>

      <Section title="Constraints">
        {(memory.constraints || []).map((constraint, index) => (
          <div key={index} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, padding: 10, border: "1px solid rgba(15,23,42,0.08)", borderRadius: 10, marginBottom: 8, background: "rgba(15,23,42,0.02)" }}>
            <Select label="Type" value={constraint.type || ""} options={["TRAVEL", "TERRAIN_ACCESS", "SCHEDULE", "TIME_LIMIT", "EQUIPMENT_ACCESS", "WEATHER_ENVIRONMENT", "LIFE_STRESS", "RECOVERY", "OTHER"]} onChange={(value) => {
              const next = [...(memory.constraints || [])];
              next[index] = { ...next[index], type: value };
              patchMemory({ constraints: next });
            }} />
            <Input label="Summary" value={constraint.summary || ""} onChange={(value) => {
              const next = [...(memory.constraints || [])];
              next[index] = { ...next[index], summary: value };
              patchMemory({ constraints: next });
            }} />
            <Input label="Start date" value={constraint.startDate || ""} onChange={(value) => {
              const next = [...(memory.constraints || [])];
              next[index] = { ...next[index], startDate: value };
              patchMemory({ constraints: next });
            }} />
            <Input label="Expires at (epoch seconds)" type="number" value={constraint.expiresAt || ""} onChange={(value) => {
              const next = [...(memory.constraints || [])];
              next[index] = { ...next[index], expiresAt: value };
              patchMemory({ constraints: next });
            }} />
            <Button tone="ghost" onClick={() => patchMemory({ constraints: (memory.constraints || []).filter((_, itemIndex) => itemIndex !== index) })}>
              Remove
            </Button>
          </div>
        ))}
        <Button onClick={() => patchMemory({ constraints: [...(memory.constraints || []), { type: "OTHER", summary: "", status: "ACTIVE", startDate: "", expiresAt: "" }] })}>
          + Add constraint
        </Button>
      </Section>

      <Section title="Coaching intents" accent>
        {(memory.intents || []).length === 0 ? (
          <div style={{ color: "var(--stone-700)" }}>No active coaching intents.</div>
        ) : (memory.intents || []).map((intent) => (
          <div key={intent.intentId || intent.summary} style={{ padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.04)", marginBottom: 8 }}>
            <div style={{ fontWeight: 750 }}>{intent.summary || intent.type}</div>
            <div style={{ marginTop: 4, color: "var(--stone-700)", fontSize: 13 }}>
              {[intent.type, intent.status, intent.distance, intent.targetDate].filter(Boolean).join(" · ")}
            </div>
          </div>
        ))}
      </Section>

      <Section title="Stimulus response profiles">
        {(memory.stimulusProfiles || []).length === 0 ? (
          <div style={{ color: "var(--stone-700)" }}>No reviewed stimulus evidence yet.</div>
        ) : (memory.stimulusProfiles || []).map((stimulus) => (
          <div key={stimulus.stimulusProfileId || stimulus.stimulusType} style={{ padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.04)", marginBottom: 8 }}>
            <div style={{ fontWeight: 750 }}>{stimulus.stimulusType || "Training stimulus"}: {stimulus.responseDirection || "UNKNOWN"}</div>
            <div style={{ marginTop: 4, color: "var(--stone-700)", fontSize: 13 }}>
              {[stimulus.sampleCount != null ? `${stimulus.sampleCount} samples` : null, stimulus.confidence != null ? `${Math.round(stimulus.confidence * 100)}% confidence` : null].filter(Boolean).join(" · ")}
            </div>
            {stimulus.summary && <div style={{ marginTop: 6 }}>{stimulus.summary}</div>}
          </div>
        ))}
      </Section>

      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        {!isExisting && <Button onClick={handleCreate} loading={saving}>Create</Button>}
        {isExisting && <Button tone="secondary" onClick={handleUpdate} loading={saving}>Update</Button>}
      </div>
      <div style={{ marginTop: 8, color: "var(--stone-700)" }}>{status}</div>
    </Card>
  );

  return (
    <>
      <TwoColumn left={mode === "search" ? renderSearch() : renderEdit()} right={<div />} />
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </>
  );
}
